import { useCallback, useMemo, useState } from 'react';

import { storage } from '@/lib/storage';
import { toIsoDate } from '@/lib/utils';

import type { DateRange } from '../dashboard.api';

export type RangePreset = 'today' | 'week' | 'month' | 'previous-month' | 'custom';

/** Presets distintos del rango personalizado. */
export type RangePresetRapido = Exclude<RangePreset, 'custom'>;

export const RANGE_LABELS: Record<RangePreset, string> = {
  today: 'Hoy',
  week: '7 días',
  month: 'Este mes',
  'previous-month': 'Mes anterior',
  custom: 'Personalizado',
};

/**
 * Máximo de días que admite el rango personalizado.
 *
 * Los indicadores se calculan sobre `pts_pump_transactions`, que en una estación
 * con movimiento tiene cientos de miles de filas: sin tope, un rango mal elegido
 * bloquearía el dashboard varios segundos.
 */
export const MAX_DIAS_RANGO = 366;

/** Clave donde se recuerda el último rango usado. */
const STORAGE_KEY = 'dashboard:date-range';

interface EstadoRango {
  preset: RangePreset;
  desde: string;
  hasta: string;
}

const PATRON_ISO = /^\d{4}-\d{2}-\d{2}$/;

const esIsoValido = (valor: unknown): valor is string =>
  typeof valor === 'string' && PATRON_ISO.test(valor);

const esPreset = (valor: unknown): valor is RangePreset =>
  typeof valor === 'string' && valor in RANGE_LABELS;

const hoyIso = (): string => toIsoDate(new Date());

/** Días calendario entre dos fechas ISO (ambas inclusive). */
export function contarDias(desde: string, hasta: string): number {
  const inicio = new Date(`${desde}T00:00:00`).getTime();
  const fin = new Date(`${hasta}T00:00:00`).getTime();

  if (!Number.isFinite(inicio) || !Number.isFinite(fin)) return 0;

  return Math.round((fin - inicio) / 86_400_000) + 1;
}

function resolverPreset(preset: RangePresetRapido): DateRange {
  const today = new Date();

  if (preset === 'today') {
    const iso = toIsoDate(today);
    return { from: iso, to: iso };
  }

  if (preset === 'week') {
    const start = new Date(today);
    start.setDate(start.getDate() - 6);
    return { from: toIsoDate(start), to: toIsoDate(today) };
  }

  if (preset === 'previous-month') {
    // Mes calendario completo anterior: útil cuando el mes en curso aún no tiene
    // movimientos registrados.
    const firstDay = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    const lastDay = new Date(today.getFullYear(), today.getMonth(), 0);
    return { from: toIsoDate(firstDay), to: toIsoDate(lastDay) };
  }

  const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  return { from: toIsoDate(firstDayOfMonth), to: toIsoDate(today) };
}

/** Rango personalizado ya normalizado (sin fechas futuras y con el tope de días). */
function resolverPersonalizado(
  desde: string,
  hasta: string,
): { range: DateRange; aviso: string | null } {
  const hoy = hoyIso();

  let to = esIsoValido(hasta) ? hasta : hoy;
  let from = esIsoValido(desde) ? desde : to;

  if (to > hoy) to = hoy;
  if (from > to) from = to;

  if (contarDias(from, to) > MAX_DIAS_RANGO) {
    const inicio = new Date(`${to}T00:00:00`);
    inicio.setDate(inicio.getDate() - (MAX_DIAS_RANGO - 1));

    return {
      range: { from: toIsoDate(inicio), to },
      aviso: `El rango se limitó a ${MAX_DIAS_RANGO} días para no degradar el dashboard.`,
    };
  }

  return { range: { from, to }, aviso: null };
}

function leerGuardado(): EstadoRango | null {
  const raw = storage.getRaw(STORAGE_KEY);
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw) as Partial<EstadoRango>;

    if (!parsed || !esIsoValido(parsed.desde) || !esIsoValido(parsed.hasta)) return null;

    return {
      preset: esPreset(parsed.preset) ? parsed.preset : 'month',
      desde: parsed.desde,
      hasta: parsed.hasta,
    };
  } catch {
    return null;
  }
}

function estadoInicial(initial: RangePreset): EstadoRango {
  const guardado = leerGuardado();
  if (guardado) return guardado;

  const preset = initial === 'custom' ? 'month' : initial;
  const rango = resolverPreset(preset);

  return { preset, desde: rango.from, hasta: rango.to };
}

/**
 * Rango de fechas del dashboard: presets rápidos o un rango personalizado.
 *
 * El último rango elegido se recuerda en `localStorage`, porque volver a
 * seleccionar las fechas cada vez que se navega a otra pantalla resulta molesto.
 */
export function useDateRange(initial: RangePreset = 'month') {
  const [estado, setEstado] = useState<EstadoRango>(() => estadoInicial(initial));

  const actualizar = useCallback((parcial: Partial<EstadoRango>) => {
    setEstado((prev) => {
      const siguiente = { ...prev, ...parcial };
      storage.setRaw(STORAGE_KEY, JSON.stringify(siguiente));
      return siguiente;
    });
  }, []);

  const setPreset = useCallback(
    (preset: RangePreset) => {
      if (preset === 'custom') {
        // Empieza el rango personalizado con lo que el usuario está viendo.
        actualizar({ preset });
        return;
      }

      const rango = resolverPreset(preset);
      actualizar({ preset, desde: rango.from, hasta: rango.to });
    },
    [actualizar],
  );

  const setCustom = useCallback(
    (rango: DateRange) => {
      const from = esIsoValido(rango.from) ? rango.from : rango.to;
      const to = esIsoValido(rango.to) ? rango.to : rango.from;

      // Si el usuario mueve "desde" más allá de "hasta", se arrastra "hasta":
      // el rango elegido siempre es válido y se ve tal como se escribió.
      actualizar({ preset: 'custom', desde: from, hasta: from > to ? from : to });
    },
    [actualizar],
  );

  const { range, aviso } = useMemo(() => {
    if (estado.preset !== 'custom') {
      return { range: resolverPreset(estado.preset), aviso: null };
    }

    return resolverPersonalizado(estado.desde, estado.hasta);
  }, [estado.preset, estado.desde, estado.hasta]);

  return {
    preset: estado.preset,
    setPreset,
    /** Rango personalizado tal como lo eligió el usuario. */
    custom: { from: estado.desde, to: estado.hasta },
    setCustom,
    /** Rango efectivo que se consulta al API (ya normalizado). */
    range,
    /** Aviso cuando el rango personalizado se recortó. */
    aviso,
  };
}
